import { Result } from '@/domain/protocols/Result'

/**
 * Marca se uma lista sincronizada com o servidor já pode ser lida como a lista "de verdade" do usuário.
 *
 * Os serviços de sync (cartas e KML/KMZ) só rodam o primeiro sync `INITIAL_SYNC_DELAY_MS` depois do boot.
 * Até lá o repositório está vazio (cartas ficam só em memória) ou desatualizado (geometrias num aparelho
 * sem cache), e quem lê a lista não consegue distinguir "ainda não carregou" de "a conta não tem nada".
 *
 * `ensure` resolve essa ambiguidade para quem precisa dela (ex.: Meus Conteúdos):
 * - já houve um sync bem-sucedido → resolve na hora;
 * - o primeiro sync ainda não terminou → espera por ele e devolve o resultado dele;
 * - o primeiro sync terminou com falha → tenta de novo (é o que um "Tentar novamente" espera).
 */
export class InitialSyncGate {
  private hasSucceeded = false
  private hasSettled = false
  private resolveFirstSettled: (result: Result<void>) => void
  private readonly firstSettled: Promise<Result<void>>

  constructor() {
    this.firstSettled = new Promise((resolve) => {
      this.resolveFirstSettled = resolve
    })
  }

  /** Registra o fim de um sync, com sucesso ou falha. */
  record(result: Result<void>): void {
    if (result.isSuccess) this.hasSucceeded = true
    if (this.hasSettled) return

    this.hasSettled = true
    this.resolveFirstSettled(result)
  }

  /** No logout os dados locais são apagados: a próxima leitura precisa de um sync novo. */
  reset(): void {
    this.hasSucceeded = false
  }

  async ensure(runSync: () => Promise<Result<void>>): Promise<Result<void>> {
    if (this.hasSucceeded) return Result.ok()
    if (!this.hasSettled) return this.firstSettled

    return runSync()
  }
}
